import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as toastr from 'toastr';

import paymentActions from './paymentActions';
import policyActions from '../policy/policyActions';

import PaymentDetailsForm from './PaymentDetailsForm';
import Fieldset from '../components/forms/Fieldset';
import { Button } from '../components/buttons';
import { IconChevronLeft } from '../components/icons';
import Utils, { PaymentStatus } from '../common/utils';

interface Props {
	baseUrl?: string;
	className?: string;
	card: StripeCard;
	invoiceCard: StripeCard;
	details: any;
	payment: PaymentHistory;
	policy: CustomerPolicy;
	stripe: any;
	user: UserDetails;
	actions: any;
	history: any;
	match: any;
}
interface State {
	isPageLoading: boolean;
	isPaymentLoading: boolean;
	paymentType: string;
}
class InvoiceDetails extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			isPageLoading: true,
			isPaymentLoading: false,
			paymentType: ''
		};
	}

	componentDidMount() {
		const userId = this.props.match.params.customerId || this.props.match.params.userId;
		const invoiceNumber = this.props.match.params.pageId || this.props.match.params.invoiceNumber;
		this.props.actions.fetchCustomerCard(userId)
		.then(() => this.props.actions.fetchCustomerPayment(userId, invoiceNumber))
		// invoice object itself doesn't hold the payment info, so if customer's card changes then last 4 card digits do not match
		.then(() => this.props.actions.fetchInvoiceCard(this.props.payment.stripeInvoiceId))
		.then(() => this.props.actions.fetchCustomerPolicy(userId, this.props.payment.policyId))
		.then(() => this.setState({ isPageLoading: false }))
		.then(() => Utils.setDocTitle(`Invoice ${this.props.payment.stripeInvoiceNumber}`));
	}

	makePayment = () => {
		this.setState({ isPaymentLoading: true });
		var promise = new Promise((resolve, reject) => {
			if (this.state.paymentType === 'existing') {
				resolve(this.props.actions.payInvoice(this.props.payment.stripeInvoiceId));
			}
			else {
				this.props.stripe.createToken({ name: this.props.details.ccCardholderName })
					.then((result: any) => {
						if (result.error) {
							throw result.error.message;
						} else {
							// Send the token to server
							resolve(this.props.actions.payInvoice(
								this.props.payment.stripeInvoiceId,
								result.token,
								this.props.user.stripeCustomerId
							));
						}
					});
			}
		});

		promise
			.then(() => {
				this.setState({ isPaymentLoading: false });
				return this.props.actions.fetchCustomerPayments(this.props.match.params.customerId);
			})
			.then(() => {
				if (this.props.baseUrl) {
					this.props.history.push(this.props.baseUrl);
				} else {
					this.props.history.push(`/user/${this.props.match.params.userId}/billing/view-bills`);
				}
			})
			.catch((errorMessage: string) => {
				toastr.error(errorMessage);
				this.setState({ isPaymentLoading: false });
			});
	}

	printInvoice = () => {
		window.print();
	}

	render() {
		return (
			<div className="content-set content-set-lg print-only">
				<Fieldset className={this.props.className}>
					<div className="invoice-header">
						<h2 className="text-center">
							Mosaic Life
						</h2>						
					</div>
					<ul className="list-unstyled">
							<li className="text-center">
								320 Sioux Road, Sherwood Park, AB 
							</li>
							<li className="text-center">
								T8A 3X6, 1-855-643-6945
							</li>
					</ul>
					<ul className="list-unstyled invoice-info">
						<li className="mr-4">Invoice Date: {Utils.formatDate(this.props.payment.date)}</li>
						<li>Invoice #: {this.props.payment.stripeInvoiceNumber}</li>
					</ul>
					<table className="table invoice-table mb-0">
						<thead className="text-muted">
							<tr>
								<th colSpan={2} className="invoice-table-desc">
									Description
								</th>
								<th className="text-right">Amount</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td colSpan={2}>Policy #: {this.props.policy.number}</td>
								<td className="text-right">
									{this.state.isPageLoading ? '-' : Utils.formatCurrency(this.props.payment.amount)}
								</td>
							</tr>
							<tr>
								<td colSpan={2} className="text-muted pl-2">
									<strong>Coverage Amount: </strong>
								</td>
								<td className="text-right text-nowrap">
									{this.state.isPageLoading ? '-' : Utils.formatCurrency(this.props.policy.coverageAmount, 0)}
								</td>
							</tr>
							<tr>
								<td colSpan={2} className="text-muted pl-2">
									<strong>Start Date: </strong>
								</td>
								<td className="text-right text-nowrap" >
									{this.state.isPageLoading ? '-' : (this.props.policy.effectiveDate)}
								</td>
							</tr>
							<tr>
								<td colSpan={2} className="text-muted pl-2">
									<strong>End Date: </strong>
								</td>
								<td className="text-right text-nowrap">
									{this.state.isPageLoading ? '-' : (this.props.policy.endDate)}
								</td>
							</tr>
							{this.props.payment.tax1 ? (
								<tr>
									<td className="hidden invoice-table-cell-hidden invoice-table-cell-no-border" />
									<td className="text-muted invoice-table-cell-no-border pl-0">
										<strong>Tax</strong>
									</td>
									<td className="text-right invoice-table-cell-no-border">
										{this.state.isPageLoading ? '-' : Utils.formatCurrency(this.props.payment.tax1)}
									</td>
								</tr>
							) : null}
							<tr>
								<td colSpan={2}>
									<strong>Total</strong>
								</td>
								<td className="text-right">
									<strong>
										{this.state.isPageLoading ?
											'-' : Utils.formatCurrency(this.props.payment.amount! + this.props.payment.tax1!)}
									</strong>
								</td>
							</tr>
							{this.props.payment.statusId === PaymentStatus.PaymentSucceeded && (
								<tr className="large">
									<td className="hidden" />
									<td className="pl-0">
										<strong>Paid</strong>
									</td>
									<td className="text-right">
										<strong>
											{this.state.isPageLoading ?
												'-' : Utils.formatCurrency(this.props.payment.amount! + this.props.payment.tax1!)}
										</strong>
									</td>
								</tr>
							)}
						</tbody>
					</table>
					<ul className="list-unstyled">
						<li className="text-right">{this.props.invoiceCard.brand}-{this.props.invoiceCard.last4}</li>
						<li className="text-right">
							{this.props.payment.statusId === PaymentStatus.Created && (
								<strong className="invoice-status text-warning">Payment pending</strong>
							)}
							{this.props.payment.statusId === PaymentStatus.PaymentSucceeded && (
								<h4 className="invoice-status text-success">Paid</h4>
							)}
							{this.props.payment.statusId === PaymentStatus.PaymentFailed && (
								<strong className="invoice-status text-danger">Payment failed</strong>
							)}
						</li>
					</ul>
					{this.props.payment.statusId !== PaymentStatus.PaymentSucceeded &&
						this.props.payment.statusId !== PaymentStatus.Created ? (
							<section className="invoice-payment">
								<h2 className="h5 text-center">Pay your bill</h2>
								<div className="workflow-progress-actions my-3">
									<Button
										className={`btn btn-outline-secondary workflow-progress-action${
											this.state.paymentType === 'existing' ? ' active' : ''
											}`}
										onClick={() => this.setState({ paymentType: 'existing' })}
									>
										Use existing card
									</Button>
									<Button
										className={`btn btn-outline-secondary workflow-progress-action${
											this.state.paymentType === 'new' ? ' active' : ''
											}`}
										onClick={() => this.setState({ paymentType: 'new' })}
									>
										Use a new card
									</Button>
								</div>
								<PaymentDetailsForm
									details={this.props.details}
									actions={this.props.actions}
									hidden={this.state.paymentType !== 'new'}
								/>
								<div className="text-center mt-4">
									<Button
										className="btn-primary"
										type="button"
										onClick={this.makePayment}
										disabled={this.state.isPaymentLoading || !this.state.paymentType}
									>
										Pay now
									</Button>
								</div>
							</section>
						) : null}
				</Fieldset>
				<div className="workflow-progress-actions no-print">
					<Button
						className="btn-outline-primary mr-2 workflow-progress-action"
						type="button"
						to={this.props.baseUrl || `/user/${this.props.match.params.userId}/billing/view-bills`}
					>
						<IconChevronLeft />
						<span className="align-middle mx-2 d-none d-sm-inline-block">Back</span>
					</Button>
					<Button
						className="btn-outline-primary mr-3"
						type="button"
						onClick={this.printInvoice}
					>
						<span className="align-middle mx-3 d-none d-sm-inline-block">Print</span>
					</Button>
				</div>
			</div>
		);
	}
}
function mapStateToProps(state: any) {
	return {
		card: state.payment.card,
		invoiceCard: state.payment.invoiceCard,
		details: state.payment.details,
		payment: state.payment.payment,
		policy: state.policy.customerPolicy,
		stripe: state.payment.stripe,
		user: state.user.user
	};
}
function mapDispatchToProps(dispatch: any) {
	const actions = { ...paymentActions, ...policyActions };
	return {
		actions: bindActionCreators(actions, dispatch)
	};
}
export default connect(mapStateToProps, mapDispatchToProps)(InvoiceDetails);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/payment/InvoiceDetails.tsx